import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap, toArray } from 'rxjs/operators';
import { formatSignedAt } from '../../utils/date.utils';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { SignService } from '../../services/sign.service';
import { UpqueueService } from '../../services/upqueue.service';
import { DeviceCryptoService } from '../../services/device-crypto.service';
import { ToastService } from '../../../shared/services/toast.service';
import { acceptJobsToPayload } from './index';
import { decodeEncryptedDocument } from './adapter.utils';
import type { Submission } from '../../models/submission.model';

// ─── Internal types ─────────────────────────────────────────────────────────

/**
 * 4xx statuses that must NOT be treated as permanent, despite being 4xx.
 *
 * @remarks
 * A `permanent` classification **deletes the queue item** (see
 * {@link UpqueueAdapter.handleResults}) — and that item holds the only copy of
 * the officer's photos, notes and GPS. So the bar for "permanent" is not "the
 * server said 4xx", it is "retrying this exact payload can never succeed".
 * These three fail that test:
 *
 * - **408 Request Timeout** and **429 Too Many Requests** are explicitly
 *   retryable by definition — the payload was never rejected on its merits.
 * - **413 Payload Too Large** is the important one (2026-08-05). Evidence
 *   photos are stored and transmitted at full capture resolution — no
 *   client-side downscale (see the "NO CLIENT-SIDE PHOTO COMPRESSION" note in
 *   `photo.service.ts`) — so an over-size body is a realistic outcome against
 *   an unknown server limit. Deleting the submission would silently destroy
 *   field evidence that cannot be recaptured, and `handleResults()` suppresses
 *   its warning toast entirely under `background: true` (every auto-sync), so
 *   the officer would get no indication at all. Keeping it queued means it
 *   stays visible on the Upqueue screen and recoverable.
 *
 * **Known trade-off:** a genuinely un-shrinkable payload now retries on every
 * sync cycle instead of being dropped once. That is deliberate — a repeating
 * failure the officer can see beats silent evidence loss. If this becomes
 * noisy, the fix is a surfaced failed-submission state (the gap `CLAUDE.md`
 * already documents), not re-deleting the item.
 */
const RETRYABLE_4XX = new Set([408, 413, 429]);

/**
 * Whether a failure status means "retrying this payload can never succeed".
 *
 * @param status - HTTP status, or `0` for a network-level failure.
 * @returns `true` only for a genuinely permanent rejection.
 */
function isPermanentStatus(status: number): boolean {
  if (status < 400 || status >= 500) return false; // 5xx + status-0 network: transient.
  if (status === 401) return false;                // Auth interceptor owns refresh/retry.
  return !RETRYABLE_4XX.has(status);
}

/**
 * Outcome of a single transmission — used to partition results after `toArray()`.
 *
 * @remarks
 * `permanent` is `true` when the server returned a 4xx error, excluding 401 and
 * the {@link RETRYABLE_4XX} statuses. These items are removed from the queue
 * immediately — retrying the same payload will never succeed. 5xx, status-0
 * (network), 401 and the retryable 4xx set are transient and kept for the next
 * sync cycle.
 */
interface TransmitResult {
  item:      Submission;
  success:   boolean;
  /** `true` for a permanently invalid payload — see {@link isPermanentStatus}. */
  permanent: boolean;
}

/** Extracts a `{ latitude, longitude }` object from a stored payload's `location` field. */
function extractCoords(
  payload: Record<string, unknown>,
): { latitude: number; longitude: number } | null {
  const loc = payload['location'] as { latitude?: unknown; longitude?: unknown } | undefined;
  if (loc == null) return null;
  const lat = Number(loc['latitude']);
  const lng = Number(loc['longitude']);
  return !isNaN(lat) && !isNaN(lng) ? { latitude: lat, longitude: lng } : null;
}

/** Extracts `affidavit_sign_status` (`'A'` | `'R'`) from a stored affidavit payload's first item. */
function extractSignStatus(payload: Record<string, unknown>): string | undefined {
  const items = payload['signed_affidavits'] as { affidavit_sign_status?: string }[] | undefined;
  return items?.[0]?.affidavit_sign_status;
}

/** Options for {@link UpqueueAdapter.transmitAll}. */
export interface TransmitOptions {
  /**
   * When `true`, suppresses the "Nothing pending to upqueue." info toast.
   * Use during automated sync cycles to avoid noisy toasts on empty queues.
   */
  quiet?: boolean;
  /**
   * When `true`, suppresses ALL result toasts (empty, success, failure, permanent).
   * Use during automated background sync cycles (`fullSync`, `autoSync`) so the
   * process server does not see error alerts on every dashboard refresh.
   * The upqueue badge still reflects the pending count visually.
   */
  background?: boolean;
}

// ─── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Transmits all pending submissions from {@link UpqueueService} to the correct
 * Tinite API endpoints, processing them sequentially to preserve submission order.
 *
 * @remarks
 * **Routing:**
 * - `"attempt"` items → `POST /process-server/{serverId}/jobs/upqueue`
 * - `"affidavit"` items → `POST /process-server/{serverId}/jobs/esignqueue`
 *
 * **Partial-failure guarantee:** Successful and permanently-failed (4xx) items
 * are removed via {@link UpqueueService.removeItems}. Transient failures (5xx,
 * status-0) remain in the store and are eligible for the next `transmitAll()`.
 *
 * **Toast messages (matches UX spec):**
 * - All succeeded         → success green  `"Submission received."`
 * - Transient failures    → danger red     `"N submitted, M failed — tap Upqueue to retry."`
 * - Permanent 4xx removed → warning yellow `"N submission(s) permanently rejected..."`
 * - Empty queue           → dark brand     `"Nothing pending to upqueue."`
 * - `background: true`    → all toasts suppressed; upqueue badge shows count visually.
 *
 * @example
 * ```ts
 * inject(UpqueueAdapter).transmitAll().subscribe();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class UpqueueAdapter {
  private readonly http           = inject(HttpClient);
  private readonly authService    = inject(AuthService);
  private readonly upqueueService = inject(UpqueueService);
  private readonly signService    = inject(SignService);
  private readonly toastService   = inject(ToastService);
  private readonly deviceCrypto   = inject(DeviceCryptoService);

  /**
   * `true` while a {@link transmitAll} call is actively processing queue items.
   *
   * @remarks
   * Exposed as a public Signal so all callers — the Upqueue page button,
   * `NetworkService` auto-retry, and `SyncService` flush — share a single
   * source of truth for in-flight state. Prevents concurrent transmissions from
   * posting the same item twice.
   */
  readonly transmitting = signal(false);

  /**
   * Transmits all pending items from {@link UpqueueService} to the API.
   *
   * @remarks
   * Items are processed sequentially via `concatMap` so that the second API
   * call does not begin until the first completes. This preserves chronological
   * order and avoids overwhelming the server with concurrent requests.
   *
   * The Observable always completes — errors on individual items are caught per-item
   * and recorded as failures rather than terminating the stream.
   *
   * @param options - Optional transmission options.
   * @param options.quiet      - Suppress the empty-queue info toast.
   * @param options.background - Suppress ALL result toasts (for background sync cycles).
   * @returns Observable that emits `void` once all items have been processed.
   *   Returns immediately with `of(void 0)` (no HTTP call, no toast) when a
   *   transmission is already in progress — preventing duplicate POSTs.
   */
  transmitAll(options?: TransmitOptions): Observable<void> {
    if (this.transmitting()) return of(void 0 as void);

    const queue = this.upqueueService.items();

    if (queue.length === 0) {
      if (!options?.quiet && !options?.background) {
        void this.toastService.info('Nothing pending to upqueue.');
      }
      return of(void 0 as void);
    }

    const token = this.authService.token();
    if (!token) return of(void 0 as void);

    const serverId = token.user.entityId;

    this.transmitting.set(true);
    return from(queue).pipe(
      concatMap((item) =>
        this.transmitOne(item, serverId).pipe(
          map(() => ({ item, success: true, permanent: false }) as TransmitResult),
          catchError((err: unknown) => {
            const status = err instanceof HttpErrorResponse ? err.status : 0;
            return of({ item, success: false, permanent: isPermanentStatus(status) } as TransmitResult);
          }),
        ),
      ),
      toArray(),
      tap((results) => this.handleResults(results, options)),
      map(() => void 0 as void),
      finalize(() => this.transmitting.set(false)),
    );
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /**
   * Routes a single queued item to the correct API endpoint.
   *
   * @param item     - The submission to transmit.
   * @param serverId - Authenticated process server entity ID.
   */
  private transmitOne(item: Submission, serverId: number): Observable<void> {
    if (item.type === 'attempt') {
      return this.transmitAttempt(item, serverId);
    }
    if (item.type === 'accept') {
      return this.transmitAccept(item, serverId);
    }
    return this.transmitAffidavit(item, serverId);
  }

  /**
   * Transmits an attempt submission to `POST /jobs/upqueue`.
   * The stored payload is a single `UpQueueJobItem`; it is wrapped in
   * `{ jobs: [...] }` as the batch endpoint requires.
   *
   * @param item     - Queued attempt submission.
   * @param serverId - Process server entity ID.
   */
  private transmitAttempt(item: Submission, serverId: number): Observable<void> {
    return this.http
      .post<unknown>(
        `${environment.apiUrl}/process-server/${serverId}/jobs/upqueue`,
        { jobs: [item.payload] },
      )
      .pipe(map(() => void 0 as void));
  }

  /**
   * Transmits a queued accept submission to `POST /jobs/acceptqueue`.
   * The stored payload contains `job_numbers: number[]`.
   *
   * @param item     - Queued accept submission.
   * @param serverId - Process server entity ID.
   */
  private transmitAccept(item: Submission, serverId: number): Observable<void> {
    const jobNums = (item.payload['job_numbers'] as number[]) ?? [];
    return this.http
      .post<unknown>(
        `${environment.apiUrl}/process-server/${serverId}/jobs/acceptqueue`,
        acceptJobsToPayload(jobNums),
      )
      .pipe(map(() => void 0 as void));
  }

  /**
   * Transmits an affidavit submission and updates {@link SignService} on
   * confirmed success — `markSigned()` for an approval, `markRejectedByPS()`
   * for a rejection (determined by the queued payload's
   * `affidavit_sign_status`).
   *
   * @remarks
   * **Decrypts the device-encrypted document before POSTing (2026-08-05).**
   * `AffidavitAdapter.buildQueuedPayload()` wraps `affidavit_sign_document`
   * with a device-bound key before enqueueing, purely to protect it at rest
   * in `IDB_STORE_QUEUE` — the server has no knowledge of that extra layer and
   * expects exactly the original CRYPTOJSKEY-ciphertext string
   * `AffidavitComponent.submitAffidavit()` produced. So this method must undo
   * the device-level wrapping (via {@link decodeEncryptedDocument} +
   * `DeviceCryptoService.decrypt()`) before the HTTP call, using a shallow-
   * cloned payload — `item.payload` itself, and therefore what
   * `extractSignStatus()`/`extractCoords()` read below, is left untouched.
   *
   * @param item     - Queued affidavit submission.
   * @param serverId - Process server entity ID.
   */
  private transmitAffidavit(item: Submission, serverId: number): Observable<void> {
    return from(this.decryptAffidavitDocument(item.payload)).pipe(
      switchMap(payload =>
        this.http.post<unknown>(
          `${environment.apiUrl}/process-server/${serverId}/jobs/esignqueue`,
          payload,
        ),
      ),
      tap(() => {
        if (extractSignStatus(item.payload) === 'R') {
          this.signService.markRejectedByPS(item.jobId);
          return;
        }
        const coords = extractCoords(item.payload);
        this.signService.markSigned(item.jobId, formatSignedAt(new Date(item.timestamp)), coords);
      }),
      map(() => void 0 as void),
    );
  }

  /**
   * Undoes {@link AffidavitAdapter}'s device-level encryption of
   * `affidavit_sign_document`, if present, returning a payload safe to POST.
   *
   * @remarks
   * `decodeEncryptedDocument()` returns `null` when the field was never
   * device-encrypted in the first place — a rejection record (no document at
   * all), or the rare case where `buildQueuedPayload()`'s own encryption step
   * failed and left the plain CRYPTOJSKEY-ciphertext value in place. Both are
   * treated identically here: the payload is sent through unchanged.
   *
   * **A genuine decrypt failure is left to reject and propagate.** This can
   * only really happen if `IDB_STORE_CRYPTO_KEYS` were cleared between
   * enqueue and transmit — which does not happen mid-session (see
   * `DeviceCryptoService`'s doc comment: the key is generated once and cached
   * for the service's lifetime) and cannot happen across a restart either,
   * since the ONLY thing that clears that store is
   * `AuthService.clearAllStorage()` on logout, which also clears
   * `IDB_STORE_QUEUE` in the same operation — so an affidavit encrypted with a
   * now-gone key can never still be sitting in the queue. If this ever
   * rejects anyway, `transmitAll()`'s `catchError` classifies a non-
   * `HttpErrorResponse` as status `0` — the same "transient, kept for the next
   * sync cycle" bucket as a network failure — never a permanent deletion; the
   * alternative (silently sending raw ciphertext, or dropping the submission)
   * would either corrupt the server record or destroy the officer's only copy
   * of a signed legal document.
   *
   * @param payload - The queued item's stored payload.
   * @returns The same payload, with `affidavit_sign_document` restored to its
   *          original (server-expected) value when it had been device-encrypted.
   */
  private async decryptAffidavitDocument(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const affidavits = payload['signed_affidavits'] as Array<Record<string, unknown>> | undefined;
    const item = affidavits?.[0];
    const encoded = item?.['affidavit_sign_document'];
    if (!item || typeof encoded !== 'string') return payload;

    const encrypted = decodeEncryptedDocument(encoded);
    if (!encrypted) return payload; // Not device-encrypted — send as-is.

    const plaintext = await this.deviceCrypto.decrypt(encrypted); // may reject — see this method's doc comment.
    return {
      ...payload,
      signed_affidavits: [{ ...item, affidavit_sign_document: plaintext }],
    };
  }

  /**
   * Removes successful and permanently-failed items from {@link UpqueueService}
   * and shows the appropriate result toast.
   *
   * @remarks
   * Permanent failures are removed immediately — retrying the same payload will
   * never succeed. Transient failures (5xx, network, 401, and the
   * {@link RETRYABLE_4XX} statuses 408/413/429) are kept for the next sync
   * cycle; see {@link isPermanentStatus} for why 413 in particular must never
   * be treated as permanent. **Removal here destroys the only copy of the
   * item's photos, notes and GPS**, which is why that classification is
   * deliberately conservative. When `options.background` is `true`, all toasts are suppressed
   * so background auto-sync does not interrupt the process server with alerts.
   *
   * @param results - Array of per-item transmission outcomes from `toArray()`.
   * @param options - Transmission options forwarded from {@link transmitAll}.
   */
  private handleResults(results: TransmitResult[], options?: TransmitOptions): void {
    const succeeded = results.filter((r) => r.success).map((r) => r.item);
    const permanent = results.filter((r) => !r.success && r.permanent).map((r) => r.item);
    const transient = results.filter((r) => !r.success && !r.permanent);

    this.upqueueService.removeItems([...succeeded, ...permanent]);

    if (options?.background) return;

    if (permanent.length > 0) {
      void this.toastService.warning(
        `${permanent.length} submission(s) permanently rejected by server — removed from queue.`,
      );
    }
    if (transient.length === 0 && succeeded.length > 0 && permanent.length === 0) {
      void this.toastService.success('Submission received.');
    } else if (transient.length > 0) {
      void this.toastService.error(
        `${succeeded.length} submitted, ${transient.length} failed — tap Upqueue to retry.`,
      );
    }
  }
}
